import {
  BaseCurrency,
  GetTransferPaymentDetailsResponse,
  isDefined,
  Loadable,
  LoadableState,
  NbSelectItemOption,
  TransferPaymentItem,
  TransferPaymentTargetAccount,
  useDictionary,
} from '@eih-netbroker/common';
import axios, { isAxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useAccounts } from '../../../shared/services/useAccounts';
import { CashActionStep, getTargetAccountAborterAtom } from '../states/cashActionAtoms';
import { usePayment } from './usePayment';

export const useTransfer = () => {
  const { formatDictionaryType } = useDictionary();
  const { getMainClientId } = useAccounts();
  const {
    setCashActionStep,
    cashActionTemp,
    cashActionStep,
    setCashActionTemp,
    getTransferPaymentDetails,
    getTransferPaymentTargetAccounts,
    submitTransferPayment,
    confirmTransferPayment,
    setTempId,
    tempId,
    setReceivedMessages,
    handleTwoFactorError,
    clearPaymentAtomState,
    smsCodeError,
    canRestartTwoFactor,
    handleTwoFactorRestart,
  } = usePayment();
  const [transferDetails, setTransferDetails] = useState<LoadableState<GetTransferPaymentDetailsResponse>>(
    Loadable.uninitialized,
  );
  const [targetAccounts, setTargetAccounts] = useState<LoadableState<Array<TransferPaymentTargetAccount>>>(
    Loadable.uninitialized,
  );
  const [selectedTransferCurrency, setSelectedTransferCurrency] = useState<string>('');
  const [selectedSourceAccount, setSelectedSourceAccount] = useState<TransferPaymentItem | undefined>(undefined);
  const [selectedTargetAccount, setSelectedTargetAccount] = useState<TransferPaymentTargetAccount | undefined>(
    undefined,
  );
  const { translation } = useLocale();
  const [getTargetAccountAborter, setGetTargetAccountAborter] = useAtom(getTargetAccountAborterAtom);

  const sourceAccountOptions = useMemo<LoadableState<Array<NbSelectItemOption<TransferPaymentItem>>>>(() => {
    if (transferDetails.isLoading) {
      return Loadable.loading;
    }

    if (transferDetails.hasError) {
      return Loadable.failed;
    }

    if (transferDetails.isLoaded) {
      return Loadable.loaded(
        transferDetails.transferPaymentItems
          .filter(acc => acc.currencyCode === selectedTransferCurrency)
          .map(acc => ({
            id: acc,
            name: `${formatDictionaryType(acc.accountCategory, 'accountCategoryTypes')} - ${acc.clientId}`,
          })),
      );
    }

    return Loadable.uninitialized;
  }, [formatDictionaryType, selectedTransferCurrency, transferDetails]);

  const targetAccountOptions = useMemo<LoadableState<Array<NbSelectItemOption<TransferPaymentTargetAccount>>>>(() => {
    if (targetAccounts.isLoading) {
      return Loadable.loading;
    }

    if (targetAccounts.hasError) {
      return Loadable.failed;
    }

    if (targetAccounts.isLoaded) {
      return Loadable.loaded(
        targetAccounts
          .filter(acc => acc.currencyCode === selectedTransferCurrency)
          .map(acc => ({
            id: acc,
            name: `${formatDictionaryType(acc.accountCategory, 'accountCategoryTypes')} - ${acc.clientId}`,
          })),
      );
    }

    return Loadable.uninitialized;
  }, [targetAccounts, formatDictionaryType, selectedTransferCurrency]);

  const currencyOptions = useMemo<Array<NbSelectItemOption<string>>>(
    () =>
      transferDetails.isLoaded
        ? Array.from(new Set(transferDetails.transferPaymentItems.map(c => c.currencyCode))).map(c => ({
            id: c,
            name: translation.portfolio.currencies[c as keyof typeof translation.portfolio.currencies] ?? c,
          }))
        : [],
    [translation, transferDetails],
  );

  const handleTransferSubmit = useCallback(
    async (onConfirm: () => void) => {
      if (
        !isDefined(cashActionTemp) ||
        !isDefined(cashActionTemp.sourceAccountCategory) ||
        !isDefined(cashActionTemp.targetAccountCategory)
      ) {
        return;
      }

      try {
        const resp = await submitTransferPayment({
          submitTransferPaymentRequest: {
            amount: parseFloat(cashActionTemp.amount),
            targetAccountCategory: cashActionTemp.targetAccountCategory,
            targetClientId: cashActionTemp.targetClientId,
            currencyCode: cashActionTemp.currencyCode,
            sourceAccountCategory: cashActionTemp.sourceAccountCategory,
            sourceClientId: cashActionTemp.sourceClientId,
          },
        });

        if (isDefined(resp.data.messages) && resp.data.messages.length > 0) {
          if (resp.data.responseType === 'submitted') {
            setTempId(resp.data.tempId);
          }
          setReceivedMessages(resp.data.messages);
          setCashActionStep(CashActionStep.ReceivedMessages);
          return;
        }

        if (resp.data.responseType === 'submitted') {
          setTempId(resp.data.tempId);
          setCashActionStep(CashActionStep.Confirm);
        } else {
          onConfirm();
        }
      } catch (error) {
        setReceivedMessages([{ msgType: 'error', msg: translation.cashAction.errorsCommon.cannotSubmit }]);
        setCashActionStep(CashActionStep.ReceivedMessages);
      }
    },
    [cashActionTemp, submitTransferPayment, setTempId, setCashActionStep, setReceivedMessages, translation],
  );

  const handleTransferConfirm = useCallback(
    async (smsCode: string, onSuccess: () => void) => {
      if (
        !isDefined(cashActionTemp) ||
        !isDefined(tempId) ||
        !isDefined(cashActionTemp.targetAccountCategory) ||
        !isDefined(cashActionTemp.sourceAccountCategory)
      ) {
        return;
      }

      try {
        const resp = await confirmTransferPayment({
          tempId,
          confirmTransferPaymentRequest: {
            smsCode,
            amount: parseFloat(cashActionTemp.amount),
            targetAccountCategory: cashActionTemp.targetAccountCategory,
            targetClientId: cashActionTemp.targetClientId,
            currencyCode: cashActionTemp.currencyCode,
            sourceClientId: cashActionTemp.sourceClientId,
            sourceAccountCategory: cashActionTemp.sourceAccountCategory,
          },
        });

        if (isDefined(resp.data.messages) && resp.data.messages.length > 0) {
          setTempId(undefined);
          setReceivedMessages(resp.data.messages);
          setCashActionStep(CashActionStep.ReceivedMessages);
          return;
        }

        if (resp.data.responseType === 'confirmed') {
          onSuccess();
        }
      } catch (error) {
        handleTwoFactorError(error);
      }
    },
    [
      cashActionTemp,
      confirmTransferPayment,
      handleTwoFactorError,
      setCashActionStep,
      setReceivedMessages,
      setTempId,
      tempId,
    ],
  );

  const getTargetAccounts = useCallback(
    async (sourceAccount: TransferPaymentItem, abortController: AbortController) => {
      setTargetAccounts(Loadable.loading);
      setSelectedTargetAccount(undefined);

      try {
        const targetAccounts = (
          await getTransferPaymentTargetAccounts(
            {
              clientId: sourceAccount.clientId,
              currencyCode: sourceAccount.currencyCode,
              accountCategory: sourceAccount.accountCategory,
            },
            abortController,
          )
        ).data.transferPaymentTargetAccounts;

        setTargetAccounts(Loadable.loaded(targetAccounts));
        const optionalTBSZAccount = targetAccounts.find(acc => acc.accountCategory.toLowerCase().startsWith('tbsz'));
        setSelectedTargetAccount(optionalTBSZAccount ?? (targetAccounts.length > 0 ? targetAccounts[0] : undefined));
      } catch (error) {
        if (isAxiosError(error) && axios.isCancel(error)) {
          return;
        }

        setTargetAccounts(Loadable.failed);
        setSelectedTargetAccount(undefined);
      }
    },
    [getTransferPaymentTargetAccounts, setSelectedTargetAccount, setTargetAccounts],
  );

  const refreshTransferDetails = useCallback(async () => {
    getTargetAccountAborter?.abort();

    setTransferDetails(Loadable.loading);
    try {
      const resp = (await getTransferPaymentDetails(getMainClientId())).data;
      setTransferDetails(Loadable.loaded(resp));

      if (resp.transferPaymentItems.length > 0) {
        setSelectedTransferCurrency(resp.transferPaymentItems[0].currencyCode);
        setSelectedSourceAccount(resp.transferPaymentItems[0]);
        const abortController = new AbortController();
        setGetTargetAccountAborter(abortController);
        getTargetAccounts(resp.transferPaymentItems[0], abortController);
      } else {
        setSelectedTransferCurrency('');
        setSelectedSourceAccount(undefined);
        setSelectedTargetAccount(undefined);
        setTargetAccounts(Loadable.loaded([]));
      }
    } catch {
      setTransferDetails(Loadable.failed);
    }
  }, [
    getTargetAccounts,
    getMainClientId,
    setTransferDetails,
    getTransferPaymentDetails,
    setGetTargetAccountAborter,
    getTargetAccountAborter,
  ]);

  const handleModalClose = useCallback(
    (onClose: () => void) => {
      clearPaymentAtomState();
      setSelectedTransferCurrency(BaseCurrency);
      onClose();
    },
    [clearPaymentAtomState, setSelectedTransferCurrency],
  );

  const handleSourceAccountChange = useCallback(
    (value: string | TransferPaymentItem) => {
      getTargetAccountAborter?.abort();
      if (typeof value === 'string' || !transferDetails.isLoaded) {
        setTargetAccounts(Loadable.loaded([]));
        setSelectedTargetAccount(undefined);
        setSelectedSourceAccount(undefined);
        return;
      }

      setSelectedSourceAccount(value);
      const abortController = new AbortController();
      setGetTargetAccountAborter(abortController);
      getTargetAccounts(value, abortController);
    },
    [getTargetAccounts, setGetTargetAccountAborter, transferDetails, getTargetAccountAborter],
  );

  const handleTargetAccountChange = useCallback(
    (value: string | TransferPaymentTargetAccount) => {
      if (typeof value === 'string') {
        setSelectedTargetAccount(undefined);
        return;
      }

      setSelectedTargetAccount(value);
    },
    [setSelectedTargetAccount],
  );

  return {
    handleTransferConfirm,
    handleTransferSubmit,
    handleModalClose,
    refreshTransferDetails,
    handleSourceAccountChange,
    handleTargetAccountChange,
    setCashActionTemp,
    setCashActionStep,
    setSelectedTransferCurrency,
    selectedSourceAccount,
    currencyOptions,
    selectedTargetAccount,
    targetAccounts,
    sourceAccountOptions,
    targetAccountOptions,
    cashActionTemp,
    cashActionStep,
    selectedTransferCurrency,
    smsCodeError,
    transferDetails,
    canRestartTwoFactor,
    handleTwoFactorRestart,
  };
};
